/**
 * The **encounter population planner** (PRD #794).
 *
 * A generated floor is twenty-odd thousand walkable tiles of rooms, corridors,
 * loops, junctions, and optional branches. Until this module existed the whole
 * of it held exactly one skeleton, because `startDungeonCombat` picked one
 * encounter zone out of the blueprint and threw the rest away.
 *
 * This is the pure half of the answer: given a blueprint and a seed, it returns
 * the **entire ordinary population of the floor** as a value record. It touches
 * no world, spawns nothing, reads no entity, and draws only from the seeded
 * stream it is handed, which is what makes every density, spacing, quota, and
 * safety invariant a corpus test rather than a play session.
 *
 * Four ideas carry the design.
 *
 * **Spacing is the density contract.** Anchors are placed across eligible
 * navigable space at a declared spacing and a declared tiles-per-pack rate, so a
 * floor feels equally inhabited whether the generator handed it twenty-four or
 * thirty-one thousand tiles. {@link ARPG_MAX_PACKS_PER_FLOOR} and
 * {@link ARPG_MAX_MONSTERS_PER_FLOOR} are the safety rail behind the knob, not
 * the knob: a pathological seed is bounded rather than tuned.
 *
 * **Packs are role compositions.** A template declares bounded counts by role -
 * `melee-pursuer`, `ranged-kiter`, `telegraphed-caster` - and a family maps each
 * role onto an archetype. That indirection is the whole reason Deceit can be
 * undead and Destard dragonkin without a second template table: swap the family,
 * keep the grammar.
 *
 * **Elites are promotions.** An eligible member of an ordinary pack is promoted
 * in place. It keeps its role, its pack, and its position, and gains bounded
 * stat, presentation, experience, and reward upgrades. Nothing about a promotion
 * turns a pack into a set piece.
 *
 * **Geography modifies the plan.** Depth from the entrance, the semantic kind of
 * the area an anchor sits in, and landmark status are declared modifiers on
 * local pack size and elite chance, so a deep landmark carries a pressure peak
 * and an early corridor does not.
 *
 * The planner deliberately does **not** decide what a monster does. Behaviour is
 * the brain seam's (PRD #793), mechanics are the catalog's, and spawning is the
 * dungeon adapter's. What comes back from here is a description.
 */

import type {
  DungeonBlueprint,
  DungeonMarker,
  DungeonRegion,
  DungeonRoom,
  DungeonRoomRole,
} from './dungeon';
import { arpgMonsterArchetype, type ArpgMonsterArchetypeId } from './arpgMonsters';
import type { BrainId } from './combat/brains';
import type { TilePoint } from './mobile';
import { SeededRng } from './rng';

/**
 * A pack role, which is a brain id and deliberately nothing else.
 *
 * Templates name roles rather than species because the same three behaviours
 * drive every family the bestiary grows. A role is therefore exactly the set of
 * brains that ship, and the guard test in `arpgPopulation.test.ts` pins that
 * every role a template names is mapped by every declared family.
 */
export type ArpgPackRole = BrainId;

export type ArpgPackTemplateId =
  | 'melee-swarm'
  | 'mixed-melee-ranged'
  | 'caster-escort'
  /**
   * The boss's dedicated single-member pack (PRD #837, slice 5). Not a
   * declared template: it is never drawn, never re-stamped, and appears only
   * when a caller declares a boss for the floor.
   */
  | 'boss';

export type ArpgMonsterFamilyId = 'ratmen';

/** The semantic kind of the area an anchor sits in. */
export type ArpgAreaKind = DungeonRoomRole | 'corridor';

/** How many navigable tiles one pack is worth. The primary density knob. */
export const ARPG_TILES_PER_PACK = 500;

/** The distance the planner tries to keep between pack anchors, in tiles. */
export const ARPG_PACK_TARGET_SPACING_TILES = 14;

/**
 * The distance two pack anchors are never closer than, in tiles.
 *
 * Ten tiles is deliberately under the twelve-tile detection radius: two packs
 * are independently pullable because activation does not chain, not because the
 * player can stand where only one of them can see. Positioning decides how much
 * danger a pull takes on, which is user story four.
 */
export const ARPG_PACK_MIN_SPACING_TILES = 10;

/** The hard per-floor pack budget. A rail for unusual seeds, not a target. */
export const ARPG_MAX_PACKS_PER_FLOOR = 64;

/** The hard per-floor monster budget, which bounds simulation cost. */
export const ARPG_MAX_MONSTERS_PER_FLOOR = 240;

/**
 * How far from the entrance nothing is placed, in tiles.
 *
 * Longer than the detection radius by a clear margin, because the guarantee the
 * player is owed is not "no monster is adjacent" but "nothing can have noticed
 * me while the floor was loading" (user story eighteen).
 */
export const ARPG_ENTRANCE_SAFE_RADIUS_TILES = 16;

/** The same guarantee around the exit, which is the other lifecycle transition. */
export const ARPG_EXIT_SAFE_RADIUS_TILES = 12;

/** How far a pack's members spread from their anchor, in tiles. */
export const ARPG_PACK_SPREAD_TILES = 4;

/** The share of a floor's depth past which local budgets start to grow. */
const DEPTH_RAMP_START = 0.35;

/** Bounded multipliers a promotion applies. Danger legible without new mechanics. */
export const ARPG_ELITE_HEALTH_MULTIPLIER = 3;
export const ARPG_ELITE_DAMAGE_MULTIPLIER = 1.6;
export const ARPG_ELITE_XP_MULTIPLIER = 4;

/** One bounded count of one role inside a template. */
export interface ArpgPackRoleBand {
  role: ArpgPackRole;
  min: number;
  max: number;
}

/**
 * One pack template: bounded counts by role and where it likes to sit.
 *
 * A template is content. It names no archetype, no bestiary row, and no number
 * that is not a count, so the first family and the fifth read the same grammar.
 */
export interface ArpgPackTemplate {
  id: ArpgPackTemplateId;
  label: string;
  bands: readonly ArpgPackRoleBand[];
  /**
   * Relative selection weight per area kind, defaulting to {@link weight}. A
   * corridor is a bad place for an escort formation and a good one for a swarm,
   * and saying so here keeps the placement loop free of area special cases.
   */
  weight: number;
  areaWeights?: Partial<Record<ArpgAreaKind, number>>;
  /** Which roles a promotion may pick from. A caster escort promotes its caster. */
  eliteRoles: readonly ArpgPackRole[];
}

export const ARPG_PACK_TEMPLATES: readonly ArpgPackTemplate[] = [
  {
    id: 'melee-swarm',
    label: 'Swarm',
    bands: [{ role: 'melee-pursuer', min: 3, max: 5 }],
    weight: 5,
    areaWeights: { corridor: 8, junction: 6 },
    eliteRoles: ['melee-pursuer'],
  },
  {
    id: 'mixed-melee-ranged',
    label: 'Mixed pack',
    bands: [
      { role: 'melee-pursuer', min: 2, max: 3 },
      { role: 'ranged-kiter', min: 1, max: 2 },
    ],
    weight: 4,
    areaWeights: { corridor: 2 },
    eliteRoles: ['melee-pursuer', 'ranged-kiter'],
  },
  {
    id: 'caster-escort',
    label: 'Caster escort',
    bands: [
      { role: 'telegraphed-caster', min: 1, max: 1 },
      { role: 'melee-pursuer', min: 2, max: 3 },
      { role: 'ranged-kiter', min: 0, max: 1 },
    ],
    weight: 3,
    // An escort wants a room to hold formation in. A corridor turns it into a
    // queue, which is the one shape the composition cannot read as protection.
    areaWeights: { corridor: 1, landmark: 5 },
    eliteRoles: ['telegraphed-caster'],
  },
];

/**
 * A dungeon's family mapping: which archetype wears each role.
 *
 * This is the seam the PRD asks for in one sentence. A dungeon declares a
 * family; the templates never change.
 */
export interface ArpgMonsterFamily {
  id: ArpgMonsterFamilyId;
  label: string;
  roles: Readonly<Record<ArpgPackRole, ArpgMonsterArchetypeId>>;
}

export const ARPG_MONSTER_FAMILIES: readonly ArpgMonsterFamily[] = [
  {
    id: 'ratmen',
    label: 'Ratmen',
    roles: {
      'melee-pursuer': 'ratman',
      'ranged-kiter': 'ratman-archer',
      'telegraphed-caster': 'ratman-mage',
    },
  },
];

export function arpgMonsterFamily(id: string): ArpgMonsterFamily | null {
  return (
    ARPG_MONSTER_FAMILIES.find((family) => family.id === id) ?? null
  );
}

export function arpgPackTemplate(id: string): ArpgPackTemplate | null {
  return ARPG_PACK_TEMPLATES.find((template) => template.id === id) ?? null;
}

/** Where a pack sits, in the blueprint's own semantic vocabulary. */
export interface ArpgPackArea {
  kind: ArpgAreaKind;
  /** The room or connection id this anchor belongs to. */
  id: string;
  /** The landmark name, when the area carries one. */
  landmark?: string;
}

export interface ArpgPackMemberPlan {
  id: string;
  role: ArpgPackRole;
  archetype: ArpgMonsterArchetypeId;
  position: TilePoint;
  /** A promoted member: bounded multipliers, a guaranteed reward, same role. */
  elite: boolean;
}

export interface ArpgPackPlan {
  id: string;
  template: ArpgPackTemplateId;
  family: ArpgMonsterFamilyId;
  anchor: TilePoint;
  area: ArpgPackArea;
  /** Room-graph distance from the entrance room. Corridors inherit the nearer end. */
  depth: number;
  members: readonly ArpgPackMemberPlan[];
  /** The reward id this pack guards, for a guarded cache. */
  guards?: string;
}

export type ArpgRewardKind = 'guarded-cache' | 'elite-hunt';

/**
 * One designated rewarded dead end.
 *
 * A guarded cache is a container that opens only once its named guardian packs
 * are cleared. An elite hunt pays through a promoted monster instead, so the
 * detour is not always the same shape (user story sixteen).
 */
export interface ArpgRewardPlan {
  id: string;
  kind: ArpgRewardKind;
  roomId: string;
  position: TilePoint;
  /** Packs that must be cleared before a cache opens. Empty for an elite hunt. */
  guardianPacks: readonly string[];
  /** The promoted member that carries an elite hunt's payout. */
  elite?: string;
}

export interface ArpgPopulationDiagnostics {
  /** Eligible navigable tiles, after safety radii and reachability. */
  navigableTiles: number;
  /** Total navigable tiles before eligibility filtering. */
  floorTiles: number;
  packs: number;
  monsters: number;
  elites: number;
  /** The pack count spacing and density asked for, before the budget rail. */
  requestedPacks: number;
  /** True when a budget rail, rather than spacing, decided the count. */
  budgetLimited: boolean;
  /** Realized nearest-neighbour spacing between anchors, in tiles. */
  minSpacingTiles: number;
  meanSpacingTiles: number;
  /** Realized density, in navigable tiles per pack. */
  tilesPerPack: number;
  templates: Record<ArpgPackTemplateId, number>;
  roles: Record<ArpgPackRole, number>;
  areas: Record<string, number>;
  rewards: Record<ArpgRewardKind, number>;
  /** Rooms that hold no pack at all, which is a coverage gap worth seeing. */
  emptyRooms: number;
}

/**
 * Where the boss stands (PRD #837, slice 5): the binding a slay-boss objective
 * reads without walking the packs. The boss itself is an ordinary planned
 * member, so its spawn, promotion, activation, death, and reward all ride the
 * machinery every other monster already uses.
 */
export interface ArpgBossPlan {
  /** The dedicated pack's id. */
  pack: string;
  /** The boss member's id inside that pack. */
  member: string;
  archetype: ArpgMonsterArchetypeId;
  /** The boss landmark room: the landmark deepest from the entrance. */
  roomId: string;
  position: TilePoint;
}

export interface ArpgPopulationPlan {
  seed: number;
  family: ArpgMonsterFamilyId;
  packs: readonly ArpgPackPlan[];
  rewards: readonly ArpgRewardPlan[];
  /** Present only when the caller declared a boss for this floor. */
  boss?: ArpgBossPlan;
  /**
   * The level every planned monster spawns and derives its stats at, present
   * only when the caller declared one (PRD #837 slice 7). Stat input, never a
   * draw: a plan with and without it holds exactly the same packs.
   */
  monsterLevel?: number;
  diagnostics: ArpgPopulationDiagnostics;
}

export interface PlanArpgPopulationOptions {
  blueprint: DungeonBlueprint;
  /** Defaults to the blueprint's own seed, which is the reproducible identity. */
  seed?: number;
  family?: ArpgMonsterFamilyId;
  /** Campaign depth, which scales pack size and elite chance. Defaults to one. */
  floorDepth?: number;
  /**
   * The level the floor's monsters are planned and scaled at (PRD #837
   * slice 7): a campaign node's per-tier `monsterLevel`, riding the plan into
   * every spawn's stat derivation. Absent, every archetype fights at its own
   * declared base level, which is what every pre-campaign floor did.
   */
  monsterLevel?: number;
  /**
   * A boss archetype to anchor in the boss landmark room (PRD #837, slice 5).
   *
   * Declared by a campaign node rather than defaulted, appended after every
   * seeded draw the ordinary population makes, so a floor planned with and
   * without its boss holds exactly the same ordinary packs.
   */
  boss?: ArpgMonsterArchetypeId;
}

function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

function regionCells(region: DungeonRegion): TilePoint[] {
  const cells: TilePoint[] = [];
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) cells.push({ x, y });
  }
  return cells;
}

/**
 * The exact semantic floor of one room.
 *
 * Authored footprints are the truth when the blueprint carries them. A legacy or
 * hand-built blueprint that only carries a storage envelope falls back to it,
 * which is what keeps the Combat Lab's single square plannable.
 */
function roomCells(room: DungeonRoom): TilePoint[] {
  return room.footprint && room.footprint.length > 0
    ? room.footprint
    : regionCells(room.region);
}

const NEIGHBOURS: readonly TilePoint[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

interface NavigableFloor {
  /** Every navigable tile keyed by position, with the area that owns it. */
  areas: Map<string, ArpgPackArea>;
  /**
   * Tiles the generator set aside for something other than a fight.
   *
   * They stay part of the navigable floor, because a run walks over them and a
   * pack on the far side of one has to be reachable through it, and they are
   * excluded from placement rather than from the map.
   */
  reserved: Set<string>;
  /** Tiles reachable on foot from the entrance, in deterministic order. */
  reachable: TilePoint[];
  reachableKeys: Set<string>;
}

/**
 * Reduce a blueprint to the navigable floor plus the semantic area of every
 * tile on it.
 *
 * Rooms claim their tiles first and corridors fill the rest, so a socket cell
 * shared by both reads as the room it belongs to.
 */
function navigableFloor(blueprint: DungeonBlueprint): NavigableFloor {
  const areas = new Map<string, ArpgPackArea>();
  const reserved = new Set<string>();
  for (const room of blueprint.rooms) {
    for (const zone of room.reservedZones) {
      for (const cell of regionCells(zone)) reserved.add(tileKey(cell.x, cell.y));
    }
  }
  for (const room of blueprint.rooms) {
    for (const cell of roomCells(room)) {
      const key = tileKey(cell.x, cell.y);
      if (areas.has(key)) continue;
      areas.set(key, {
        kind: room.role,
        id: room.id,
        ...(room.landmark ? { landmark: room.landmark } : {}),
      });
    }
  }
  for (const connection of blueprint.connections) {
    for (const cell of connection.footprint ?? []) {
      const key = tileKey(cell.x, cell.y);
      if (areas.has(key)) continue;
      areas.set(key, { kind: 'corridor', id: connection.id });
    }
  }

  // Reachability from the entrance, over the navigable set itself. A tile the
  // player cannot walk to is a tile nothing is placed on, which is the spawn
  // half of user story three: everything that exists is something the run can
  // actually meet.
  const start = blueprint.entrance.position;
  const reachableKeys = new Set<string>();
  const reachable: TilePoint[] = [];
  const startKey = tileKey(start.x, start.y);
  if (areas.has(startKey)) {
    reachableKeys.add(startKey);
    const pending: TilePoint[] = [{ x: start.x, y: start.y }];
    while (pending.length > 0) {
      const current = pending.shift()!;
      reachable.push(current);
      for (const step of NEIGHBOURS) {
        const next = { x: current.x + step.x, y: current.y + step.y };
        const key = tileKey(next.x, next.y);
        if (!areas.has(key) || reachableKeys.has(key)) continue;
        reachableKeys.add(key);
        pending.push(next);
      }
    }
  }
  return { areas, reserved, reachable, reachableKeys };
}

/** Room-graph distance from the entrance room, per room id. */
function roomDepths(blueprint: DungeonBlueprint): Map<string, number> {
  const graph = new Map<string, string[]>(
    blueprint.rooms.map((room) => [room.id, []]),
  );
  for (const connection of blueprint.connections) {
    graph.get(connection.a.roomId)?.push(connection.b.roomId);
    graph.get(connection.b.roomId)?.push(connection.a.roomId);
  }
  const depths = new Map<string, number>([[blueprint.entrance.roomId, 0]]);
  const pending = [blueprint.entrance.roomId];
  while (pending.length > 0) {
    const current = pending.shift()!;
    for (const next of graph.get(current) ?? []) {
      if (depths.has(next)) continue;
      depths.set(next, depths.get(current)! + 1);
      pending.push(next);
    }
  }
  return depths;
}

/** The rooms on the shortest entrance-to-exit route, which is the likely path. */
function mainRouteRooms(blueprint: DungeonBlueprint): Set<string> {
  const graph = new Map<string, string[]>(
    blueprint.rooms.map((room) => [room.id, []]),
  );
  for (const connection of blueprint.connections) {
    graph.get(connection.a.roomId)?.push(connection.b.roomId);
    graph.get(connection.b.roomId)?.push(connection.a.roomId);
  }
  const previous = new Map<string, string | null>([
    [blueprint.entrance.roomId, null],
  ]);
  const pending = [blueprint.entrance.roomId];
  while (pending.length > 0) {
    const current = pending.shift()!;
    if (current === blueprint.exit.roomId) break;
    for (const next of graph.get(current) ?? []) {
      if (previous.has(next)) continue;
      previous.set(next, current);
      pending.push(next);
    }
  }
  const route = new Set<string>();
  if (!previous.has(blueprint.exit.roomId)) return route;
  for (
    let current: string | null = blueprint.exit.roomId;
    current !== null;
    current = previous.get(current) ?? null
  ) {
    route.add(current);
  }
  return route;
}

function distanceTiles(a: TilePoint, b: TilePoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function shuffle<T>(values: T[], rng: SeededRng): T[] {
  for (let index = values.length - 1; index > 0; index--) {
    const swap = rng.nextInt(index + 1);
    const held = values[index]!;
    values[index] = values[swap]!;
    values[swap] = held;
  }
  return values;
}

/**
 * Which connection end a corridor tile is nearer, expressed as a room depth.
 *
 * A corridor has no depth of its own, so it borrows the shallower of the two
 * rooms it joins. That keeps a long approach corridor from reading as deeper
 * than the room it opens into.
 */
function areaDepth(
  area: ArpgPackArea,
  blueprint: DungeonBlueprint,
  depths: Map<string, number>,
): number {
  if (area.kind !== 'corridor') return depths.get(area.id) ?? 0;
  const connection = blueprint.connections.find(
    (candidate) => candidate.id === area.id,
  );
  if (!connection) return 0;
  return Math.min(
    depths.get(connection.a.roomId) ?? 0,
    depths.get(connection.b.roomId) ?? 0,
  );
}

/** How much this area and depth add to a pack's size, in members. */
function sizeBonus(kind: ArpgAreaKind, depthRatio: number): number {
  const areaBonus = kind === 'landmark' ? 1 : 0;
  const depthBonus =
    depthRatio <= DEPTH_RAMP_START
      ? 0
      : Math.min(2, Math.round((depthRatio - DEPTH_RAMP_START) * 3));
  return areaBonus + depthBonus;
}

/** The chance a pack in this area at this depth carries a promoted member. */
function eliteChance(kind: ArpgAreaKind, depthRatio: number, floorDepth: number): number {
  const base = kind === 'landmark' ? 0.35 : kind === 'junction' ? 0.12 : 0.08;
  const scaled = base + depthRatio * 0.12 + (floorDepth - 1) * 0.02;
  return Math.min(0.5, scaled);
}

function templateWeight(
  template: ArpgPackTemplate,
  kind: ArpgAreaKind,
): number {
  return template.areaWeights?.[kind] ?? template.weight;
}

function pickTemplate(
  kind: ArpgAreaKind,
  rng: SeededRng,
): ArpgPackTemplate {
  const weights = ARPG_PACK_TEMPLATES.map((template) =>
    templateWeight(template, kind),
  );
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let roll = rng.next() * total;
  for (const [index, template] of ARPG_PACK_TEMPLATES.entries()) {
    roll -= weights[index]!;
    if (roll <= 0) return template;
  }
  return ARPG_PACK_TEMPLATES[ARPG_PACK_TEMPLATES.length - 1]!;
}

/**
 * Choose the tiles one pack's members stand on.
 *
 * A breadth-first walk out from the anchor over the navigable set, which places
 * a pack along a corridor when it is in a corridor and around a floor when it is
 * in a room, without the planner knowing which of the two it is doing. Tiles
 * already claimed by another pack are skipped, so no two monsters start on the
 * same cell.
 */
function memberTiles(
  anchor: TilePoint,
  count: number,
  floor: NavigableFloor,
  eligible: ReadonlySet<string>,
  claimed: Set<string>,
): TilePoint[] {
  const chosen: TilePoint[] = [];
  const visited = new Set<string>([tileKey(anchor.x, anchor.y)]);
  const pending: TilePoint[] = [anchor];
  while (pending.length > 0 && chosen.length < count) {
    const current = pending.shift()!;
    const key = tileKey(current.x, current.y);
    if (!claimed.has(key) && eligible.has(key)) {
      claimed.add(key);
      chosen.push(current);
    }
    if (distanceTiles(current, anchor) >= ARPG_PACK_SPREAD_TILES) continue;
    for (const step of NEIGHBOURS) {
      const next = { x: current.x + step.x, y: current.y + step.y };
      const nextKey = tileKey(next.x, next.y);
      if (visited.has(nextKey) || !floor.reachableKeys.has(nextKey)) continue;
      visited.add(nextKey);
      pending.push(next);
    }
  }
  return chosen;
}

function emptyTemplateCounts(): Record<ArpgPackTemplateId, number> {
  return { 'melee-swarm': 0, 'mixed-melee-ranged': 0, 'caster-escort': 0, boss: 0 };
}

function emptyRoleCounts(): Record<ArpgPackRole, number> {
  return { 'melee-pursuer': 0, 'ranged-kiter': 0, 'telegraphed-caster': 0 };
}

/**
 * Plan one floor's entire ordinary population.
 *
 * Pure: the same blueprint, seed, family, and depth produce an exactly equal
 * plan, which is the reproducibility contract the PRD asks for and the corpus
 * asserts. The seeded draw order is fixed and documented at each site so that a
 * later tuning change is a visible change to the plan rather than a silent one.
 */
export function planArpgPopulation(
  options: PlanArpgPopulationOptions,
): ArpgPopulationPlan {
  const { blueprint } = options;
  const seed = (options.seed ?? blueprint.seed) >>> 0;
  const familyId = options.family ?? 'ratmen';
  const family = arpgMonsterFamily(familyId);
  if (!family) throw new Error(`unknown monster family: ${familyId}`);
  const floorDepth = Math.max(1, options.floorDepth ?? 1);
  const monsterLevel = options.monsterLevel;
  if (
    monsterLevel !== undefined &&
    (!Number.isSafeInteger(monsterLevel) || monsterLevel < 1)
  ) {
    throw new Error(`monster level must be a positive integer: ${monsterLevel}`);
  }
  const rng = new SeededRng(seed ^ 0x50_4f_50_00);

  const floor = navigableFloor(blueprint);
  const depths = roomDepths(blueprint);
  const maxDepth = Math.max(1, ...depths.values());
  const route = mainRouteRooms(blueprint);

  const entrance = blueprint.entrance.position;
  const exit = blueprint.exit.position;
  const eligible = floor.reachable.filter((tile) => {
    if (floor.reserved.has(tileKey(tile.x, tile.y))) return false;
    if (distanceTiles(tile, entrance) < ARPG_ENTRANCE_SAFE_RADIUS_TILES) return false;
    if (distanceTiles(tile, exit) < ARPG_EXIT_SAFE_RADIUS_TILES) return false;
    return true;
  });

  const eligibleKeys = new Set(eligible.map((tile) => tileKey(tile.x, tile.y)));
  const requestedPacks = Math.floor(eligible.length / ARPG_TILES_PER_PACK);
  const targetPacks = Math.min(requestedPacks, ARPG_MAX_PACKS_PER_FLOOR);

  // Anchor placement. The candidate order is the one draw the placement loop
  // makes: everything after it is decided by spacing, which is what makes the
  // realized density a property of the floor rather than of the shuffle.
  const candidates = shuffle([...eligible], rng);
  const anchors: TilePoint[] = [];
  for (const candidate of candidates) {
    if (anchors.length >= targetPacks) break;
    const spaced = anchors.every(
      (anchor) => distanceTiles(anchor, candidate) >= ARPG_PACK_TARGET_SPACING_TILES,
    );
    if (spaced) anchors.push(candidate);
  }
  // A second, looser pass only if target spacing could not fill the floor. The
  // minimum is the contract; the target is the preference.
  if (anchors.length < targetPacks) {
    for (const candidate of candidates) {
      if (anchors.length >= targetPacks) break;
      const spaced = anchors.every(
        (anchor) => distanceTiles(anchor, candidate) >= ARPG_PACK_MIN_SPACING_TILES,
      );
      if (spaced) anchors.push(candidate);
    }
  }
  // Deterministic geographic order, so pack ids read along the floor rather than
  // along the shuffle, and so the quota fix-ups below are stable.
  anchors.sort((left, right) => left.y - right.y || left.x - right.x);

  const claimed = new Set<string>();
  const packs: ArpgPackPlan[] = [];
  let monsters = 0;
  for (const [index, anchor] of anchors.entries()) {
    const area = floor.areas.get(tileKey(anchor.x, anchor.y))!;
    const depth = areaDepth(area, blueprint, depths);
    const depthRatio = depth / maxDepth;
    const template = pickTemplate(area.kind, rng);
    const bonus = sizeBonus(area.kind, depthRatio);
    const roles: ArpgPackRole[] = [];
    for (const band of template.bands) {
      const count = band.min + rng.nextInt(band.max - band.min + 1);
      for (let member = 0; member < count; member++) roles.push(band.role);
    }
    // The area and depth bonus lands on the template's first band, which is the
    // composition's own idea of what it is mostly made of.
    const bulk = template.bands[0]!.role;
    for (let extra = 0; extra < bonus; extra++) roles.push(bulk);

    const remaining = ARPG_MAX_MONSTERS_PER_FLOOR - monsters;
    if (remaining <= 0) break;
    const wanted = Math.min(roles.length, remaining);
    const tiles = memberTiles(anchor, wanted, floor, eligibleKeys, claimed);
    if (tiles.length === 0) continue;
    const packId = `pack-${index}`;
    const members: ArpgPackMemberPlan[] = tiles.map((position, memberIndex) => {
      const role = roles[memberIndex]!;
      return {
        id: `${packId}:${memberIndex}`,
        role,
        archetype: family.roles[role],
        position,
        elite: false,
      };
    });
    monsters += members.length;
    packs.push({
      id: packId,
      template: template.id,
      family: family.id,
      anchor,
      area,
      depth,
      members,
    });
  }

  // Template variety is a guarantee rather than an average: an unlucky seed
  // cannot remove a core composition from a floor (user story nine). A missing
  // template is re-stamped onto the pack whose area likes it best, keeping the
  // member count it already planned so the density contract is untouched.
  for (const template of ARPG_PACK_TEMPLATES) {
    if (packs.some((pack) => pack.template === template.id)) continue;
    const host = [...packs]
      .sort(
        (left, right) =>
          templateWeight(template, right.area.kind) -
            templateWeight(template, left.area.kind) ||
          left.id.localeCompare(right.id),
      )
      .find((pack) => {
        // Never take the last copy of another template away to satisfy this one.
        const others = packs.filter(
          (candidate) => candidate.template === pack.template,
        );
        return others.length > 1;
      });
    if (!host) continue;
    const roles = restampRoles(template, host.members.length);
    host.template = template.id;
    host.members = host.members.map((member, index) => ({
      ...member,
      role: roles[index]!,
      archetype: family.roles[roles[index]!],
    }));
  }

  // Promotions. One draw per pack, in pack order, so a plan's elites are as
  // reproducible as its packs.
  const elites: ArpgPackMemberPlan[] = [];
  for (const pack of packs) {
    const depthRatio = pack.depth / maxDepth;
    if (rng.next() >= eliteChance(pack.area.kind, depthRatio, floorDepth)) continue;
    const promoted = promoteMember(pack, rng);
    if (promoted) elites.push(promoted);
  }
  // At least one elite sits on the likely traversal route, so a run that walks
  // straight to the exit still meets one (user story eleven).
  if (!packs.some((pack) => pack.members.some((member) => member.elite) && route.has(pack.area.id))) {
    const onRoute = packs.find((pack) => route.has(pack.area.id)) ?? packs[0];
    const promoted = onRoute ? promoteMember(onRoute, rng) : null;
    if (promoted) elites.push(promoted);
  }

  // Designated rewarded dead ends. Ordinary dead ends stay eligible for ordinary
  // population and get nothing extra, which is what keeps a designation worth
  // walking to (user story seventeen).
  const rewards: ArpgRewardPlan[] = [];
  for (const [index, landmark] of blueprint.landmarks.entries()) {
    const guardians = packs.filter((pack) => pack.area.id === landmark.roomId);
    const kind: ArpgRewardKind =
      guardians.length > 0 && rng.next() < 0.5 ? 'guarded-cache' : 'elite-hunt';
    const id = `reward-${index}`;
    if (kind === 'guarded-cache') {
      for (const guardian of guardians) guardian.guards = id;
      rewards.push({
        id,
        kind,
        roomId: landmark.roomId,
        position: { ...landmark.position },
        guardianPacks: guardians.map((pack) => pack.id),
      });
      continue;
    }
    // An elite hunt pays through the monster rather than through a container, so
    // it needs a pack to promote inside. A landmark with no pack of its own
    // borrows the nearest one, and a floor with no packs at all gets no reward.
    const host =
      guardians[0] ??
      [...packs].sort(
        (left, right) =>
          distanceTiles(left.anchor, landmark.position) -
          distanceTiles(right.anchor, landmark.position),
      )[0];
    if (!host) continue;
    const promoted =
      host.members.find((member) => member.elite) ?? promoteMember(host, rng);
    if (!promoted) continue;
    if (!elites.includes(promoted)) elites.push(promoted);
    rewards.push({
      id,
      kind,
      roomId: landmark.roomId,
      position: { ...landmark.position },
      guardianPacks: [],
      elite: promoted.id,
    });
  }

  // The boss (PRD #837, slice 5): appended after every seeded draw above, so a
  // declared boss never perturbs the ordinary population, and planned as one
  // dedicated elite pack so the run instantiates it with no new machinery.
  let boss: ArpgBossPlan | undefined;
  if (options.boss) {
    const archetype = arpgMonsterArchetype(options.boss);
    if (!archetype) throw new Error(`unknown boss archetype: ${options.boss}`);
    // The boss landmark room is the landmark deepest from the entrance; the
    // first declared landmark wins a tie, which keeps the choice stable.
    const landmark = blueprint.landmarks.reduce<DungeonMarker | null>(
      (best, candidate) =>
        best === null ||
        (depths.get(candidate.roomId) ?? 0) > (depths.get(best.roomId) ?? 0)
          ? candidate
          : best,
      null,
    );
    if (!landmark) {
      throw new Error('a boss floor requires a landmark room to anchor it');
    }
    // The landmark-marker fallback is defensive for the degenerate floor whose
    // boss room has no free eligible tile in reach; overlap with a claimed
    // tile is accepted there rather than failing the plan.
    const tile =
      memberTiles(landmark.position, 1, floor, eligibleKeys, claimed)[0] ??
      { ...landmark.position };
    const area: ArpgPackArea = floor.areas.get(tileKey(tile.x, tile.y)) ?? {
      kind: 'landmark',
      id: landmark.roomId,
    };
    const packId = 'boss';
    const member: ArpgPackMemberPlan = {
      id: `${packId}:0`,
      role: archetype.brain,
      archetype: archetype.id,
      position: { ...tile },
      // The elite promotion is the boss's elite-style treatment: the bounded
      // multipliers, the painted body, and the guaranteed reward all come from
      // the one promotion mechanism the encounter already owns.
      elite: true,
    };
    packs.push({
      id: packId,
      template: 'boss',
      family: family.id,
      anchor: { ...tile },
      area,
      depth: areaDepth(area, blueprint, depths),
      members: [member],
    });
    boss = {
      pack: packId,
      member: member.id,
      archetype: archetype.id,
      roomId: landmark.roomId,
      position: { ...tile },
    };
  }

  return {
    seed,
    family: family.id,
    packs,
    rewards,
    ...(boss ? { boss } : {}),
    ...(monsterLevel !== undefined ? { monsterLevel } : {}),
    diagnostics: diagnose({
      blueprint,
      floor,
      eligible: eligible.length,
      packs,
      rewards,
      requestedPacks,
    }),
  };
}

/**
 * Re-stamp a template onto a pack that already has its member count, filling the
 * template's minimum bands first and topping up with its bulk role.
 */
function restampRoles(
  template: ArpgPackTemplate,
  count: number,
): ArpgPackRole[] {
  const roles: ArpgPackRole[] = [];
  for (const band of template.bands) {
    for (let member = 0; member < band.min && roles.length < count; member++) {
      roles.push(band.role);
    }
  }
  const bulk = template.bands[0]!.role;
  while (roles.length < count) roles.push(bulk);
  return roles.slice(0, count);
}

/**
 * Promote one eligible member of an ordinary pack in place.
 *
 * The choice is a single draw among the template's elite-eligible members, so a
 * caster escort promotes its caster and a swarm promotes one of its bodies. A
 * pack that already carries a promotion is left alone: an elite is a spike
 * inside an ordinary pack, not a stack of them.
 */
function promoteMember(
  pack: ArpgPackPlan,
  rng: SeededRng,
): ArpgPackMemberPlan | null {
  if (pack.members.some((member) => member.elite)) return null;
  const template = arpgPackTemplate(pack.template);
  if (!template) return null;
  const eligible = pack.members.filter((member) =>
    template.eliteRoles.includes(member.role),
  );
  const pool = eligible.length > 0 ? eligible : pack.members;
  if (pool.length === 0) return null;
  const chosen = pool[rng.nextInt(pool.length)]!;
  const promoted: ArpgPackMemberPlan = { ...chosen, elite: true };
  pack.members = pack.members.map((member) =>
    member.id === chosen.id ? promoted : member,
  );
  return promoted;
}

function diagnose(input: {
  blueprint: DungeonBlueprint;
  floor: NavigableFloor;
  eligible: number;
  packs: readonly ArpgPackPlan[];
  rewards: readonly ArpgRewardPlan[];
  requestedPacks: number;
}): ArpgPopulationDiagnostics {
  const { packs } = input;
  const templates = emptyTemplateCounts();
  const roles = emptyRoleCounts();
  const areas: Record<string, number> = {};
  const rewardCounts: Record<ArpgRewardKind, number> = {
    'guarded-cache': 0,
    'elite-hunt': 0,
  };
  let monsters = 0;
  let elites = 0;
  for (const pack of packs) {
    templates[pack.template] += 1;
    areas[pack.area.kind] = (areas[pack.area.kind] ?? 0) + 1;
    for (const member of pack.members) {
      monsters += 1;
      roles[member.role] += 1;
      if (member.elite) elites += 1;
    }
  }
  for (const reward of input.rewards) rewardCounts[reward.kind] += 1;
  const spacings = packs.map((pack) => {
    const others = packs.filter((other) => other.id !== pack.id);
    return others.length === 0
      ? 0
      : Math.min(
          ...others.map((other) => distanceTiles(pack.anchor, other.anchor)),
        );
  });
  const occupiedRooms = new Set(
    packs.filter((pack) => pack.area.kind !== 'corridor').map((pack) => pack.area.id),
  );
  return {
    navigableTiles: input.eligible,
    floorTiles: input.floor.reachable.length,
    packs: packs.length,
    monsters,
    elites,
    requestedPacks: input.requestedPacks,
    budgetLimited:
      input.requestedPacks > ARPG_MAX_PACKS_PER_FLOOR ||
      monsters >= ARPG_MAX_MONSTERS_PER_FLOOR,
    minSpacingTiles: spacings.length === 0 ? 0 : Math.min(...spacings),
    meanSpacingTiles:
      spacings.length === 0
        ? 0
        : spacings.reduce((sum, value) => sum + value, 0) / spacings.length,
    tilesPerPack: packs.length === 0 ? 0 : input.eligible / packs.length,
    templates,
    roles,
    areas,
    rewards: rewardCounts,
    emptyRooms: input.blueprint.rooms.filter(
      (room) => !occupiedRooms.has(room.id),
    ).length,
  };
}
